import type { ApolloQueryResult } from '@apollo/client/core';
import type { GraphQLFormattedError } from 'graphql';

import type { AuthContextQuery } from '@shared/api/graphql/generated';

import { getApolloRuntimeClient } from '@/shared/api';
import { useLocationStorage, useOrganizationStorage } from '@/shared/config';
import { isNamedTimezone } from '@shared/lib/date-time';

import type {
  AuthContext,
  AuthContextFailure,
  AuthContextFailureKind,
  AuthContextLoadResult,
  Employee,
  Location,
  Organization,
  User,
} from '../model/types';

import { AUTH_CONTEXT } from './auth.graphql';

type GraphqlAuthContext = AuthContextQuery['authContext'];
type GraphqlUser = NonNullable<GraphqlAuthContext['user']>;
type GraphqlEmployee = NonNullable<GraphqlAuthContext['employee']>;
type GraphqlOrganization = NonNullable<GraphqlAuthContext['organization']>;
type GraphqlLocation = NonNullable<GraphqlAuthContext['location']>;
type GraphqlImpersonationState = AuthContextQuery['impersonationState'];
type GraphqlImpersonationUser = NonNullable<GraphqlImpersonationState['target_user']>;

const AUTH_CONTEXT_DEADLINE_MS = 8_000;
const BASIC_AUTH_RELOAD_COOLDOWN_MS = 10_000;
const BASIC_AUTH_RELOAD_STORAGE_KEY = 'auth:basic-challenge-reload-at';

const FAILURE_MESSAGES: Record<AuthContextFailureKind, string> = {
  timeout: 'Не удалось загрузить данные сессии за отведённое время. Проверьте подключение и повторите попытку.',
  network: 'Не удалось подключиться к серверу. Проверьте интернет-соединение и повторите попытку.',
  server: 'Сервис временно недоступен. Повторите попытку позже.',
  'invalid-response': 'Не удалось загрузить данные сессии. Повторите попытку.',
};

class AuthContextDeadlineError extends Error {
  constructor() {
    super('Auth context deadline exceeded');
    this.name = 'AuthContextDeadlineError';
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const findHttpStatus = (error: unknown, visited: Set<object>): number | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  if (visited.has(error)) {
    return undefined;
  }

  visited.add(error);

  const status = error.statusCode ?? error.status;
  if (typeof status === 'number' && Number.isInteger(status)) {
    return status;
  }

  return findHttpStatus(error.response, visited) ?? findHttpStatus(error.networkError, visited);
};

export const getHttpStatus = (error: unknown): number | undefined => findHttpStatus(error, new Set<object>());

const normalizeHeaderValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
    return value.join(', ');
  }

  return undefined;
};

const readWwwAuthenticateHeader = (headers: unknown): string | undefined => {
  if (!isRecord(headers)) {
    return undefined;
  }

  if (typeof headers.get === 'function') {
    const value = headers.get('www-authenticate');
    const normalizedValue = normalizeHeaderValue(value);

    if (normalizedValue !== undefined) {
      return normalizedValue;
    }
  }

  const entry = Object.entries(headers).find(([name]) => name.toLowerCase() === 'www-authenticate');

  return normalizeHeaderValue(entry?.[1]);
};

const findWwwAuthenticateHeader = (error: unknown, visited: Set<object>): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  if (visited.has(error)) {
    return undefined;
  }

  visited.add(error);

  return (
    readWwwAuthenticateHeader(error.headers) ??
    findWwwAuthenticateHeader(error.response, visited) ??
    findWwwAuthenticateHeader(error.networkError, visited)
  );
};

export const isBasicAuthChallenge = (error: unknown): boolean => {
  if (getHttpStatus(error) !== 401) {
    return false;
  }

  const authenticateHeader = findWwwAuthenticateHeader(error, new Set<object>());

  return authenticateHeader !== undefined && /^Basic(?:\s|$)/i.test(authenticateHeader.trim());
};

export const reloadForBasicAuthChallenge = (url?: string): boolean => {
  const now = Date.now();
  const storedReloadAt = sessionStorage.getItem(BASIC_AUTH_RELOAD_STORAGE_KEY);
  const previousReloadAt = storedReloadAt === null ? undefined : Number(storedReloadAt);

  if (
    previousReloadAt !== undefined &&
    Number.isFinite(previousReloadAt) &&
    now - previousReloadAt < BASIC_AUTH_RELOAD_COOLDOWN_MS
  ) {
    // eslint-disable-next-line no-console -- Диагностика защиты от reload-цикла видна на уровне Verbose.
    console.debug('[auth] повторная перезагрузка для Basic Auth подавлена', { url });

    return false;
  }

  sessionStorage.setItem(BASIC_AUTH_RELOAD_STORAGE_KEY, String(now));
  console.warn('[auth] challenge Basic Auth, перезагружаем страницу', { url });
  window.location.reload();

  return true;
};

const LOCATION_SWITCH_PATH = '/auth/location-switch';

const getApiErrorCode = (error: unknown): unknown => {
  if (!isRecord(error)) {
    return undefined;
  }

  // Apollo кладёт тело ответа в `result`, axios — в `response.data`.
  const body = error.result ?? (isRecord(error.response) ? error.response.data : undefined);

  return isRecord(body) ? body.error_code : undefined;
};

/** 403 API: в организации с локациями запрос пришёл без рабочей локации. */
export const isLocationRequiredError = (error: unknown): boolean =>
  getHttpStatus(error) === 403 && getApiErrorCode(error) === 'LOCATION_HEADER_REQUIRED';

/** 404 API: сохранённой в браузере организации больше нет (например, dev-базу пересоздали). */
export const isOrganizationNotFoundError = (error: unknown): boolean =>
  getHttpStatus(error) === 404 && getApiErrorCode(error) === 'ORGANIZATION_NOT_FOUND';

/** Забывает сохранённые организацию и локацию и перезапускает сессию без них. */
export const reloadWithoutStaleTenant = (): void => {
  console.warn('[auth] сохранённая организация не найдена, сбрасываем организацию и локацию');
  useOrganizationStorage().resetOrganizationId();
  useLocationStorage().clearLocationId();
  window.location.reload();
};

/** Полная перезагрузка на выбор локации: свежий контекст и пустой кэш Apollo. */
export const reloadForLocationSelection = (): void => {
  if (window.location.pathname === LOCATION_SWITCH_PATH) {
    return;
  }

  console.warn('[auth] рабочая локация не выбрана, перезагружаем на выбор локации');
  window.location.href = LOCATION_SWITCH_PATH;
};

const getThrownGraphqlErrors = (error: unknown): ReadonlyArray<GraphQLFormattedError> => {
  if (!isRecord(error) || !Array.isArray(error.graphQLErrors)) {
    return [];
  }

  return error.graphQLErrors as ReadonlyArray<GraphQLFormattedError>;
};

export const isAuthenticationError = (error: GraphQLFormattedError): boolean => {
  const category = error.extensions?.category;
  const exception = error.extensions?.exception;

  return (
    category === 'authentication' ||
    error.extensions?.code === 'UNAUTHENTICATED' ||
    (typeof exception === 'string' && exception.endsWith('AuthenticationException')) ||
    error.message === 'Unauthenticated.'
  );
};

const createFailure = (kind: AuthContextFailureKind, status?: number): AuthContextLoadResult => {
  const failure: AuthContextFailure = {
    kind,
    message: FAILURE_MESSAGES[kind],
  };

  if (status !== undefined) {
    failure.status = status;
  }

  return { status: 'failure', failure };
};

const classifyThrownError = (error: unknown): AuthContextLoadResult => {
  if (error instanceof AuthContextDeadlineError) {
    return createFailure('timeout');
  }

  const status = getHttpStatus(error);
  const graphqlErrors = getThrownGraphqlErrors(error);
  if (status === 401 || status === 403 || graphqlErrors.some(isAuthenticationError)) {
    return { status: 'unauthenticated' };
  }

  if (graphqlErrors.length > 0) {
    return createFailure('server', status);
  }

  return createFailure(status !== undefined && status >= 500 ? 'server' : 'network', status);
};

const queryAuthContextWithDeadline = async (): Promise<ApolloQueryResult<AuthContextQuery>> => {
  const abortController = new AbortController();
  let deadlineHandle: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<never>((_resolve, reject) => {
    deadlineHandle = setTimeout(() => {
      reject(new AuthContextDeadlineError());
      abortController.abort();
    }, AUTH_CONTEXT_DEADLINE_MS);
  });

  try {
    return await Promise.race([
      getApolloRuntimeClient().query<AuthContextQuery>({
        query: AUTH_CONTEXT,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        context: {
          sessionBootstrap: true,
          fetchOptions: { signal: abortController.signal },
        },
      }),
      deadline,
    ]);
  } finally {
    if (deadlineHandle !== undefined) {
      clearTimeout(deadlineHandle);
    }
  }
};

const toUser = (user: GraphqlUser | GraphqlImpersonationUser): User => ({
  id: user.id,
  phone: user.phone ?? '',
  first_name: user.first_name ?? null,
  last_name: user.last_name ?? null,
  patronymic_name: user.patronymic_name ?? null,
  telegram_id: 'telegram_id' in user ? (user.telegram_id ?? null) : null,
  is_super_user: user.is_super_user == null ? undefined : Number(user.is_super_user),
});

const toLocation = (location: GraphqlLocation): Location => {
  if (!isNamedTimezone(location.timezone))
    throw new RangeError('Устаревший формат часового пояса. Обновите приложение.');
  return {
    id: location.id,
    location_id: location.location_id,
    organization_id: location.organization_id,
    alias: location.alias ?? '',
    title: location.title,
    short_name: location.short_name,
    color: location.color,
    enabled: location.enabled,
    photo: location.photo ?? null,
    icon: location.icon ?? null,
    city: location.city ?? null,
    address: location.address ?? null,
    full_address: location.full_address ?? null,
    phone: location.phone ?? null,
    latitude: location.latitude ?? null,
    longitude: location.longitude ?? null,
    coordinate: [
      location.coordinate?.[0] ?? location.latitude ?? null,
      location.coordinate?.[1] ?? location.longitude ?? null,
    ],
    timezone: location.timezone,
    opened_from: location.opened_from ?? null,
    closed_from: location.closed_from ?? null,
  };
};

const toOrganization = (organization: GraphqlOrganization): Organization => ({
  id: organization.id,
  alias: organization.alias ?? '',
  title: organization.title,
  sphere_id: organization.sphere_id ?? null,
  resource_term: organization.resource_term,
  domain: organization.domain ?? null,
  enabled_modules: organization.enabled_modules,
  has_locations: organization.has_locations,
  timezone: organization.timezone,
  currency: organization.currency,
  country_code: organization.country_code ?? null,
  locations: organization.locations?.map(toLocation) ?? [],
  created_at: organization.created_at ?? undefined,
  updated_at: organization.updated_at ?? undefined,
});

const toEmployee = (employee: GraphqlEmployee): Employee => ({
  id: employee.id,
  title: employee.title ?? '',
  first_name: employee.first_name ?? '',
  last_name: employee.last_name ?? '',
  patronymic_name: employee.patronymic_name ?? null,
  phone: employee.phone ?? '',
  employee_id: employee.employee_id,
  role_id: employee.role_id ?? null,
  status: employee.status,
  fired_at: employee.fired_at ?? null,
  fired_reason: employee.fired_reason ?? null,
  created_at: employee.created_at,
  last_activity_at: employee.last_activity_at ?? null,
  has_all_locations_access: employee.has_all_locations_access,
  location_ids: employee.location_ids,
  is_owner: employee.is_owner,
});

const toAuthContext = (context: GraphqlAuthContext): AuthContext => ({
  user: context.user ? toUser(context.user) : null,
  employee: context.employee ? toEmployee(context.employee) : null,
  organization: context.organization ? toOrganization(context.organization) : null,
  location: context.location ? toLocation(context.location) : null,
  permissions: context.permissions,
  available_organizations: context.available_organizations.map(toOrganization),
  impersonation: {
    active: false,
    session_id: null,
    started_at: null,
    impersonator: null,
    target_user: null,
    organization: null,
  },
});

const toImpersonationState = (state: GraphqlImpersonationState): AuthContext['impersonation'] => ({
  active: state.active,
  session_id: state.session_id ?? null,
  started_at: state.started_at ?? null,
  impersonator: state.impersonator ? toUser(state.impersonator) : null,
  target_user: state.target_user ? toUser(state.target_user) : null,
  organization: state.organization
    ? {
        id: state.organization.id,
        alias: state.organization.alias ?? '',
        title: state.organization.title,
      }
    : null,
});

export const fetchAuthContext = async (): Promise<AuthContextLoadResult> => {
  let result: ApolloQueryResult<AuthContextQuery>;

  try {
    result = await queryAuthContextWithDeadline();
  } catch (error: unknown) {
    return classifyThrownError(error);
  }

  const networkError = result.error?.networkError;
  const networkStatus = getHttpStatus(networkError);
  if (networkError) {
    if (networkStatus === 401 || networkStatus === 403) {
      return { status: 'unauthenticated' };
    }

    return createFailure(networkStatus !== undefined && networkStatus >= 500 ? 'server' : 'network', networkStatus);
  }

  const graphqlErrors = result.errors ?? result.error?.graphQLErrors ?? [];
  if (graphqlErrors.some(isAuthenticationError)) {
    return { status: 'unauthenticated' };
  }

  if (graphqlErrors.length > 0) {
    return createFailure('server');
  }

  if (!result.data?.authContext) {
    return createFailure('invalid-response');
  }

  let context: AuthContext;
  try {
    context = {
      ...toAuthContext(result.data.authContext),
      impersonation: toImpersonationState(result.data.impersonationState),
    };
  } catch {
    return createFailure('invalid-response');
  }

  if (!context.user) {
    return { status: 'unauthenticated' };
  }

  if (!context.organization) {
    return { status: 'organization-required', context };
  }

  return { status: 'ready', context };
};
